<template>
  <div id="dcp-dealer-locator" class="dcp-dealer-locator">
    <div v-if="!isInitialized" class="dcp-dealer-locator__loading"><wb-spinner></wb-spinner></div>
    <div v-show="isInitialized" :id="containerId" ref="containerRef" class="dcp-dealer-locator__map"></div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, toRefs, watch } from 'vue';
import { infuse } from '../../../ioc-config';
import { useDealerLocatorAem } from './dealer-locator-aem.composable';
import { useDealerLocatorWidget } from './dealer-locator-widget.composable';

export default defineComponent({
  name: 'DealerLocator',
  props: {
    allowedList: {
      type: Array as PropType<string[]>,
      required: true,
      default: () => [],
    },
    autoHeight: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: true,
    },
    containerId: {
      type: String,
      default: 'emh-dealer-locator__widget-container',
    },
    hiddenFields: {
      type: Array as PropType<string[]>,
      default: () => [
        'dl-search-title',
        'dl-dealer-cta-bar',
        'dl-dealer-map',
        'dl-dealer-contacts',
        'dl-list-dealer-more-info',
      ],
    },
    searchProfileName: {
      type: String,
      required: false,
      default: infuse('config').dealerLocator.searchProfileName,
    },
    interactive: Boolean,
    preload: Boolean,
    height: String,
    removeMargins: Boolean,
    loading: {
      type: String as PropType<'lazy' | 'eager'>,
      default: 'lazy',
    },
  },
  emits: ['dealer-selected'],
  setup(props, { emit }) {
    const {
      allowedList,
      autoHeight,
      hiddenFields,
      searchProfileName,
      interactive,
      preload,
      height,
      removeMargins,
      loading,
    } = toRefs(props);

    const { dealerLocator, lang, shopCountry } = infuse('config');
    const containerRef = ref<HTMLDivElement>();

    const getConfigId = () => {
      if (interactive.value) {
        return dealerLocator.configId;
      }

      if (preload.value) {
        return dealerLocator?.preload?.configId;
      }

      return dealerLocator?.informative?.configId;
    };

    const fullHeight = computed(() => (height.value ? height.value : '720px'));

    const maxHeight = computed(() => (autoHeight.value ? '100%' : fullHeight.value));

    const dealerSelectorConfiguration = computed(() => ({
      ...(autoHeight.value ? { height: '100%' } : {}),
      height: 'auto',
      applicationId: dealerLocator.applicationId,
      configId: getConfigId(),
      market: dealerLocator.country || shopCountry,
      language: `${lang}_${shopCountry.toUpperCase()}`,
      element: containerRef.value,
      searchProfileName: searchProfileName.value,
      apiKey: dealerLocator.widgetKey,
      countries: [dealerLocator.country || shopCountry],
      removeMargins: removeMargins.value,
      loadingType: loading.value,
      selectDealerCallback: (dealer: any) => emit('dealer-selected', dealer),
      ...(hiddenFields.value.length > 0 && { hiddenFields: hiddenFields.value }),
      whiteList: allowedList.value.length > 0 ? allowedList.value : undefined,
      blackList:
        dealerLocator.blackListDealers && dealerLocator.blackListDealers.length > 0
          ? dealerLocator.blackListDealers
          : undefined,
    }));

    const dealerLocatorWidget = useDealerLocatorWidget(dealerSelectorConfiguration);

    useDealerLocatorAem();

    watch(allowedList, async () => {
      await dealerLocatorWidget.destroy();
      dealerLocatorWidget.initialize();
    });

    return { containerRef, isInitialized: dealerLocatorWidget.isInitialized, fullHeight, maxHeight };
  },
});
</script>

<style src="./dealerLocator.scss" lang="scss" scoped></style>

<style lang="scss">
.dl-dealer-card {
  padding-bottom: var(--wb-spacing-xxs);
}

.dl-deep-link-single-dealer-view--full-height {
  height: v-bind('fullHeight') !important;
}

.dl-map-view {
  max-height: v-bind('maxHeight');

  &--desktop-mode {
    height: v-bind('fullHeight') !important;
  }
}
</style>
